import { useState, useEffect } from 'react';
import { InterestData, getStoredInterestData, storeInterestData } from '@/utils/interestDataUtils';

/**
 * React hook for managing interest data with SecureStorage
 * Handles async operations and provides reactive state
 */
export const useInterestData = () => {
  const [interestData, setInterestData] = useState<InterestData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load interest data on mount
  useEffect(() => {
    const loadInterestData = async () => {
      try {
        setIsLoading(true);
        const data = await getStoredInterestData();
        setInterestData(data);
      } catch (error) {
        console.error('Error loading interest data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInterestData();

    // Listen for interest data updates
    const handleInterestDataUpdate = (event: CustomEvent) => {
      setInterestData(event.detail);
    };

    window.addEventListener('interestDataUpdated', handleInterestDataUpdate as EventListener);
    return () => {
      window.removeEventListener('interestDataUpdated', handleInterestDataUpdate as EventListener);
    };
  }, []);

  // Store interest data
  const storeData = async (newData: InterestData) => {
    try {
      await storeInterestData(newData);
      setInterestData(newData);
    } catch (error) {
      console.error('Error storing interest data:', error);
    }
  };

  return {
    interestData,
    vehicleInterest: interestData?.vehicle || null,
    isLoading,
    storeData
  };
};

/**
 * Synchronous helper for backward compatibility
 * Only use when async operations are not possible
 */
export const useInterestDataSync = () => {
  const [interestData, setInterestData] = useState<InterestData | null>(null);

  useEffect(() => {
    // Use secure storage to get the data since it's now encrypted
    const loadInterestData = async () => {
      try {
        const data = await getStoredInterestData();
        if (data) {
          setInterestData(data);
        }
      } catch (error) {
        console.error('Error loading sync interest data:', error);
      }
    };

    loadInterestData();

    // Listen for updates
    const handleUpdate = (event: CustomEvent) => {
      setInterestData(event.detail);
    };

    window.addEventListener('interestDataUpdated', handleUpdate as EventListener);
    return () => {
      window.removeEventListener('interestDataUpdated', handleUpdate as EventListener);
    };
  }, []);

  return interestData;
};